'use client'

import { useEffect, useRef } from 'react'
import * as yup from 'yup'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { Gender, GetMyInfosQuery } from '@/core/graphql/types'
import { NIR_PLACEHOLDER, isValidNir } from '@/utils/nir'
import { isPatientReady } from '@/utils/profile-readiness'

// secu = N° de sécurité sociale (NIR): exactly 15 digits when provided, and never the
// all-zeros placeholder (the "no secu yet" sentinel). Empty -> undefined so it's skipped
// unless required (France billing readiness).
const secuField = yup
  .string()
  .matches(/^\d{15}$/, 'Le numéro de sécurité sociale doit comporter 15 chiffres')
  .notOneOf([NIR_PLACEHOLDER], 'Veuillez saisir un numéro de sécurité sociale valide')
  .nullable()
  .transform((value) => value || undefined)

const baseShape = {
  birthday: yup.string().required('Date of birth is required'),
  gender: yup.string<Gender>().oneOf([Gender.M, Gender.F, Gender.O]).required('Gender is required'),
  phone: yup.string().optional(),
  secu: secuField,
}

const completeProfileSchema = yup.object(baseShape)

export type CompleteProfileFormData = yup.InferType<typeof completeProfileSchema>

/** Build the validation schema. In France mode the NIR (`secu`) is mandatory. */
export function buildCompleteProfileSchema(requireSecu = false) {
  return requireSecu ? yup.object({ ...baseShape, secu: secuField.required('Le numéro de sécurité sociale est requis') }) : completeProfileSchema
}

type UseCompleteProfileFormParams = {
  patient: GetMyInfosQuery['myInfos'] | undefined
  /** France mode: the NIR is mandatory to complete the profile. */
  isFrance: boolean
}

/**
 * Owns the complete-profile form: validation schema (France requires the NIR)
 * and seeding the fields ONCE from the patient's existing info.
 */
export function useCompleteProfileForm({ patient, isFrance }: UseCompleteProfileFormParams) {
  const form = useForm<CompleteProfileFormData>({
    resolver: yupResolver(buildCompleteProfileSchema(isFrance)),
    defaultValues: {
      birthday: '',
      gender: undefined,
      phone: '',
      secu: '',
    },
  })

  const { reset } = form
  const prefilledRef = useRef(false)

  // Seed the form ONCE when patient data first arrives. The ref guard (rather than
  // relying on effect deps) keeps a fresh data identity from re-running reset() and
  // wiping in-progress phone/NIR edits.
  useEffect(() => {
    if (!patient || prefilledRef.current || isPatientReady(patient, isFrance)) return
    prefilledRef.current = true
    reset({
      birthday: patient.userInfo?.birthday ? new Date(patient.userInfo.birthday).toISOString().split('T')[0] : '',
      // 'I' (Inconnu) is the schema default, not a real choice — treat it as unset so the
      // M/F/O select isn't stuck on an out-of-options value.
      gender: patient.userInfo?.gender && patient.userInfo.gender !== Gender.I ? (patient.userInfo.gender as CompleteProfileFormData['gender']) : undefined,
      phone: '',
      // Never pre-fill the '000…' placeholder — it must be replaced with a real NIR.
      secu: isValidNir(patient.secu) ? (patient.secu ?? '') : '',
    })
  }, [patient, reset, isFrance])

  return form
}
